import { Component, DestroyRef, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import {
    EquipmentModel,
    NotificationDataInstanceToken,
    NotificationDataInterface,
    NotificationModel,
    NotificationToGetOtherTasks,
    NotificationToModifyOtherTask,
    OtherTaskNotificationModel,
    OtherTasksService,
    ValueHelperInstanceToken,
    ValueHelperInterface,
    ConfigInstanceToken,
    ConfigInterface,
} from '@evorait/pace-core';
import { catchError, filter, finalize, map, take, tap } from 'rxjs/operators';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import moment from 'moment';
import { BehaviorSubject, EMPTY, Observable, of, Subject, switchMap } from 'rxjs';
import { otherTaskToPreview, PaceLink, PaceNotificationPreviewInterface } from '@evorait/pace-components';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'pace-equipment-other-tasks',
    templateUrl: './equipment-other-tasks.component.html',
    styleUrls: ['./equipment-other-tasks.component.scss'],
    standalone: false,
})
export class EquipmentOtherTasksComponent implements OnInit, OnDestroy {
    @Input({ required: true }) public notification: NotificationModel;

    public equipmentNotifications$: Observable<OtherTaskNotificationModel[]>;
    public customerNotifications$: Observable<OtherTaskNotificationModel[]>;
    public description: string;

    private equipmentLoadingSubject = new BehaviorSubject<boolean>(false);
    private equipmentErrorSubject = new BehaviorSubject<boolean>(false);
    public equipmentLoading$ = this.equipmentLoadingSubject.asObservable();
    public equipmentError$ = this.equipmentErrorSubject.asObservable();

    private customerLoadingSubject = new BehaviorSubject<boolean>(false);
    private customerErrorSubject = new BehaviorSubject<boolean>(false);
    public customerLoading$ = this.customerLoadingSubject.asObservable();
    public customerError$ = this.customerErrorSubject.asObservable();

    private fetchCustomerTasksTrigger$ = new Subject<void>();

    public hasCustomerId: boolean;
    public assignedTask: NotificationToModifyOtherTask | undefined;
    public successBackLink: PaceLink;
    private mainEquipment: EquipmentModel | undefined;
    private notificationParamsToGetOtherTasks: NotificationToGetOtherTasks;
    private customerId: string;
    private readonly overlapUpcomingNotification: boolean;
    private pickedTasksFromIndexedDB: string[];

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        public readonly modalInstance: BsModalRef,
        private readonly translate: TranslateService,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        @Inject(NotificationDataInstanceToken) private readonly notificationService: NotificationDataInterface,
        private readonly otherTasksService: OtherTasksService,
        @Inject(ValueHelperInstanceToken) private readonly valueHelperService: ValueHelperInterface,
        private readonly destroyRef: DestroyRef,
    ) {
        this.overlapUpcomingNotification = this.configService.getOtherTasksOverlapUpcomingNoti();
        this.successBackLink = {
            label: this.translate.instant('NOTIFICATION_DETAIL_EQUIPMENTS_OTHER_TASKS_SUCCESS_BACK'),
            icon: faArrowLeft,
            action: (): void => {
                this.modalInstance.hide();
            },
        };
    }

    public ngOnInit(): void {
        this.equipmentLoadingSubject.next(true);
        this.equipmentErrorSubject.next(false);
        this.hasCustomerId = false;
        this.mainEquipment = this.notification.getMainEquipment();
        this.notificationParamsToGetOtherTasks = this.notification.getNotificationRequestParamsForGetOtherTasks();
        this.description = this.mainEquipment ? this.mainEquipment.equiShortDesc.value : '';

        this.equipmentNotifications$ = this.otherTasksService.syncCompleted$.pipe(
            take(1),
            switchMap(() => this.otherTasksService.getAllPickedTasks()),
            tap((alreadyPickedTasks: string[]) => {
                this.pickedTasksFromIndexedDB = [...alreadyPickedTasks];
            }),
            switchMap(() => this.fetchTasksByEquipment()),
        );

        this.customerNotifications$ = this.fetchCustomerTasksTrigger$.pipe(
            tap(() => {
                this.customerLoadingSubject.next(true);
                this.customerErrorSubject.next(false);
            }),
            switchMap(() => this.fetchTasksByCustomer()),
        );

        this.otherTasksService.triggerSyncConcurrent();
    }

    public ngOnDestroy(): void {
        this.fetchCustomerTasksTrigger$.complete();
    }

    public getTypeLabel(task: NotificationToModifyOtherTask | undefined): string {
        return task ? this.valueHelperService.getNotificationTypeLabel(this.notification.notificationType) : '';
    }

    public convertToPreview(task: NotificationToModifyOtherTask): PaceNotificationPreviewInterface {
        return otherTaskToPreview(task);
    }

    private hasNotifications(notifications: OtherTaskNotificationModel[]): boolean {
        return Array.isArray(notifications) && notifications.length > 0;
    }

    private fetchTasksByEquipment(): Observable<OtherTaskNotificationModel[]> {
        if (!this.mainEquipment) {
            this.equipmentLoadingSubject.next(false);
            return of([]);
        }
        return this.otherTasksService
            .getOtherTasksByEquipment(this.notificationParamsToGetOtherTasks, this.mainEquipment.equnr.value)
            .pipe(
                catchError((error) => {
                    this.equipmentErrorSubject.next(true);
                    this.log.error(
                        'EquipmentOtherTasksComponent: getTasksByEquipment: Error while fetching Other Tasks by equipment:',
                        JSON.stringify(error, Object.getOwnPropertyNames(error)),
                    );
                    return of<OtherTaskNotificationModel[]>([]);
                }),
                filter((notifications) => this.hasNotifications(notifications)),
                tap((notifications) => {
                    this.setCustomerId(notifications[0].customer);
                }),
                map((notifications) => this.checkAndHideGetTaskButton(notifications)),
                finalize(() => this.equipmentLoadingSubject.next(false)),
            );
    }

    public triggerFetchCustomerTasks(): void {
        this.fetchCustomerTasksTrigger$.next();
    }

    private fetchTasksByCustomer(): Observable<OtherTaskNotificationModel[]> {
        return this.otherTasksService
            .getOtherTasksByCustomer(this.notificationParamsToGetOtherTasks, this.customerId)
            .pipe(
                catchError((error) => {
                    this.customerErrorSubject.next(true);
                    this.log.error(
                        'EquipmentOtherTasksComponent: getTasksByCustomer: Error while fetching Other Tasks by CustomerId:',
                        JSON.stringify(error, Object.getOwnPropertyNames(error)),
                    );
                    return of<OtherTaskNotificationModel[]>([]);
                }),
                filter((notifications) => this.hasNotifications(notifications)),
                map((notifications) => this.checkAndHideGetTaskButton(notifications)),
                finalize(() => this.customerLoadingSubject.next(false)),
            );
    }

    public getTask(taskData: NotificationToModifyOtherTask, isEquipmentTab: boolean): void {
        if (isEquipmentTab) {
            this.equipmentLoadingSubject.next(true);
            this.equipmentErrorSubject.next(false);
        } else {
            this.customerLoadingSubject.next(true);
            this.customerErrorSubject.next(false);
        }
        this.log.debug(
            `EquipmentOtherTasksComponent: getTask - params sending in POST request: ${JSON.stringify(taskData)}`,
        );
        this.otherTasksService
            .getTask(taskData)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                catchError((error) => {
                    this.log.error(
                        'EquipmentOtherTaskComponent: getTask: Error while self assigning a task from other tasks: ',
                        JSON.stringify(error, Object.getOwnPropertyNames(error)),
                    );
                    if (isEquipmentTab) {
                        this.equipmentErrorSubject.next(true);
                    } else {
                        this.customerErrorSubject.next(true);
                    }
                    return EMPTY;
                }),
                tap((assignedTask) => {
                    this.assignedTask = assignedTask;
                }),
                switchMap(() =>
                    this.otherTasksService.saveTaskOriginalPlannedDate(taskData).pipe(
                        catchError((error) => {
                            this.log.error(
                                'EquipmentOtherTaskComponent: saveTaskOriginalPlannedDate: Error while saving original planned date of picked task: ',
                                JSON.stringify(error, Object.getOwnPropertyNames(error)),
                            );
                            return EMPTY;
                        }),
                    ),
                ),
                finalize(() => {
                    if (isEquipmentTab) {
                        this.equipmentLoadingSubject.next(false);
                    } else {
                        this.customerLoadingSubject.next(false);
                    }
                }),
            )
            .subscribe();
    }

    private setCustomerId(customerId: string): void {
        this.customerId = customerId?.trim() ?? '';
        this.hasCustomerId = this.customerId.length > 0;
        if (this.hasCustomerId) {
            this.triggerFetchCustomerTasks();
        }
    }

    private getTaskDurationInSeconds(taskDuration: string): number {
        const parsedTaskDuration = parseFloat(taskDuration || '0');
        return isNaN(parsedTaskDuration) ? 0 : parsedTaskDuration;
    }

    private checkAndHideGetTaskButton(notifications: OtherTaskNotificationModel[]): OtherTaskNotificationModel[] {
        const nextNotification: NotificationModel | undefined = this.notificationService.getNextUpcomingNotification(
            this.notification,
        );
        if (!nextNotification) {
            return notifications;
        }

        const now = moment();
        const timeTillNextNotificationStartsInSeconds = nextNotification.plannedStart.diff(now, 'seconds');
        const timeTillCurrentNotificationEndsInSeconds = this.notification.plannedEnd.diff(now, 'seconds');

        return notifications.map((notification) => {
            if (notification.canGetTask) {
                const notificationTaskId = `${notification.notificationNo}::${notification.taskNo}`;

                const taskDurationInSeconds = this.getTaskDurationInSeconds(notification.duration);
                // check for picked tasks in indexed DB because gateway takes time to update
                if (this.pickedTasksFromIndexedDB?.includes(notificationTaskId)) {
                    this.log.debug(
                        `EquipmentOtherTasksComponent: checkAndHideGetTaskButton – Task[${notification.notificationNo}(${notification.taskNo})] already exists in IndexedDB and cannot be picked again.`,
                    );
                    notification.canGetTask = false;
                } else if (taskDurationInSeconds > timeTillNextNotificationStartsInSeconds) {
                    this.log.debug(
                        `EquipmentOtherTasksComponent: checkAndHideGetTaskButton - Duration of task[${notification.notificationNo}(${notification.taskNo})] i.e. ${taskDurationInSeconds}s exceeds the time left to next notification. Next Notification[${nextNotification.identifier.qmNum}(${nextNotification.identifier.qmNum})] starts on ${nextNotification.plannedStart}`,
                    );
                    notification.canGetTask = false;
                } else if (
                    taskDurationInSeconds > timeTillCurrentNotificationEndsInSeconds &&
                    !this.overlapUpcomingNotification
                ) {
                    this.log.debug(
                        `EquipmentOtherTasksComponent: checkAndHideGetTaskButton - Duration of task[${notification.notificationNo}(${notification.taskNo})] i.e. ${taskDurationInSeconds}s exceeds the time left to finish current notification[${this.notification.identifier.qmNum}(${this.notification.identifier.qmNum})] that is ending on ${this.notification.plannedEnd} and overlapping isn't allowed`,
                    );
                    notification.canGetTask = false;
                }
            }
            return notification;
        });
    }
}
